
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Tick} from './Tick';
import {FieldModelTick} from './Tick';

/**
 * TickClient struct
 */
class TickClient extends Tick {
  /**
   * Initialize struct
   * @param {!Tick=} parent
   * @param {!number=} priceChangePercent
   * @param {!number=} priceChange
   * @constructor
   */
  constructor (parent = new Tick(), priceChangePercent = 0.0, priceChange = 0.0) {
    super()
    super.copy(parent)
    this.PriceChangePercent = priceChangePercent
    this.PriceChange = priceChange
  }

  /**
   * Copy struct (shallow copy)
   * @this {!TickClient}
   * @param {!TickClient} other Other struct
   * @returns {!TickClient} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.PriceChangePercent != null) {
      this.PriceChangePercent = other.PriceChangePercent
    } else {
      this.PriceChangePercent = null
    }
    if (other.PriceChange != null) {
      this.PriceChange = other.PriceChange
    } else {
      this.PriceChange = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!TickClient}
   * @returns {!TickClient} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new TickClientModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new TickClientModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!TickClient}
   * @param {!TickClient} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof TickClient)) {
      throw new TypeError('Instance of TickClient is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!TickClient}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      PriceChangePercent: ((this.PriceChangePercent != null) ? this.PriceChangePercent : null),
      PriceChange: ((this.PriceChange != null) ? this.PriceChange : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return TickClient.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!TickClient} other Object value
   * @returns {!TickClient} Created struct
   */
  static fromObject (other) {
    return new TickClient().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!TickClient}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return TickClient.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!TickClient}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 97
  }
}

export { TickClient };

/**
 * Fast Binary Encoding TickClient field model
 */
class FieldModelTickClient extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelTick(buffer, 4 + 4)
    this.PriceChangePercent = new fbe.FieldModelDouble(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.PriceChange = new fbe.FieldModelDouble(buffer, this.PriceChangePercent.fbeOffset + this.PriceChangePercent.fbeSize)
  }

  /**
   * Get the Tick field model
   * @this {!FieldModelTickClient}
   * @returns {!FieldModelTick} Tick field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelTickClient}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelTickClient}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.PriceChangePercent.fbeSize + this.PriceChange.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelTickClient}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.PriceChangePercent.fbeExtra + this.PriceChange.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelTickClient}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelTickClient.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelTickClient}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 97
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelTickClient}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelTickClient}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.PriceChangePercent.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.PriceChangePercent.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PriceChangePercent.fbeSize

    if ((fbeCurrentSize + this.PriceChange.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.PriceChange.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PriceChange.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelTickClient}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelTickClient}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelTickClient}
   * @param {!TickClient} fbeValue Default value, defaults is new TickClient()
   * @returns {!TickClient} TickClient value
   */
  get (fbeValue = new TickClient()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelTickClient}
   * @param {!TickClient} fbeValue TickClient value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.PriceChangePercent.fbeSize) <= fbeStructSize) {
      fbeValue.PriceChangePercent = this.PriceChangePercent.get()
    } else {
      fbeValue.PriceChangePercent = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PriceChangePercent.fbeSize

    if ((fbeCurrentSize + this.PriceChange.fbeSize) <= fbeStructSize) {
      fbeValue.PriceChange = this.PriceChange.get()
    } else {
      fbeValue.PriceChange = 0.0
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.PriceChange.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelTickClient}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelTickClient}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelTickClient}
   * @param {!TickClient} fbeValue TickClient value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelTickClient}
   * @param {TickClient} fbeValue TickClient value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.PriceChangePercent.set(fbeValue.PriceChangePercent)
    this.PriceChange.set(fbeValue.PriceChange)
  }
}

export { FieldModelTickClient };

/**
 * Fast Binary Encoding TickClient model
 */
class TickClientModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelTickClient(this.buffer, 4)
  }

  /**
   * Get the TickClient model
   * @this {!TickClientModel}
   * @returns {!FieldModelTickClient} model TickClient model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!TickClientModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!TickClientModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return TickClientModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!TickClientModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelTickClient.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!TickClientModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!TickClientModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!TickClientModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!TickClientModel}
   * @param {!TickClient} value TickClient value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!TickClientModel}
   * @param {!TickClient} value TickClient value, defaults is new TickClient()
   * @return {!object} Deserialized TickClient value and its size
   */
  deserialize (value = new TickClient()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new TickClient(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new TickClient(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!TickClientModel}
   * @param {!number} prev Previous TickClient model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { TickClientModel };
